import React, { useEffect } from 'react';

import { Button } from '../../../../components/ui/button.tsx';
import { useLocale } from '../../../../lib/hooks/use-locale.ts';
import { api } from '../../../../utils/api.ts';
import { getFullName } from '../../../../utils/name.ts';

import { cn } from '../../../../lib/utils.ts';
import { SearchCombobox } from '../search-combobox/search-combobox.tsx';

type UsersSelectProps = {
    className?: string;
    onValueChange: (value: string) => void;
    value: string;
};

type User = {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    deletedAt?: Date | null;
};

export const UsersSelect = ({ className, onValueChange, value }: UsersSelectProps) => {
    const { t } = useLocale();
    const { data: platoUsers, isLoading } = api.platoUser.query.users.getManyUsers.getList.useQuery();
    const [searchQuery, setSearchQuery] = React.useState('');
    const [cleanFileredValues, setCleanFileredValues] = React.useState<User[]>([]);

    const [open, setOpen] = React.useState(false);

    const users = React.useMemo(() => {
        if (platoUsers) {
            return platoUsers
                .map((user: Omit<User, 'fullName'>) => ({
                    ...user,
                    fullName: getFullName(user.firstName, user.lastName),
                }))
                .filter((user: User) => user.fullName.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return [];
    }, [platoUsers, searchQuery]);

    useEffect(() => {
        // We filter out deleted users
        setCleanFileredValues(users.filter((user) => !user.deletedAt));
    }, [users]);

    const selectedUser: User | undefined = users.find((user: User) => user.id === value);

    return (
        <SearchCombobox<User>
            selectedValue={selectedUser?.id || ''}
            isLoading={isLoading}
            groupedItems={[
                {
                    id: 'users',
                    items: cleanFileredValues,
                },
            ]}
            keySelector={(user) => user.id}
            valueSelector={(user) => user.id}
            renderItem={(user) => <div>{user.fullName}</div>}
            onSearchQueryChange={setSearchQuery}
            searchQuery={searchQuery}
            onValueChange={onValueChange}
            open={open}
            setOpen={setOpen}
            notFoundPlaceholder={t('no_data')}
            inputPlaceholder={t('search')}
            className={className}
        >
            <Button
                variant="ghost"
                className={cn('font-normal', className)}
                type="button"
                onClick={() => {
                    setOpen((prev) => !prev);
                }}
                disabled={isLoading}
            >
                {selectedUser ? (
                    <span>{selectedUser.fullName}</span>
                ) : (
                    t('tasks__assigned_to_placeholder', {
                        ns: 'tasks',
                    })
                )}
            </Button>
        </SearchCombobox>
    );
};
